import { z } from "zod";

import { baseEntityFields, customFieldsField } from "./base.js";
import { accesRefletField } from "./acces.js";
import { mentionsSchema } from "./mentions.js";
import { estDatePure } from "./jour.js";

/**
 * Message : famille "communication individuelle".
 *
 * Communication individuelle envoyée ou reçue, tous canaux confondus (email,
 * sms, notification, portail, note d'appel), avec ses destinataires et son
 * statut de remise. Construite sur le modèle "famille + type" de la bible
 * (patron : `asset`).
 *
 * Le socle ne connaît pas la nature métier concrète du message ("rappel de
 * rendez-vous", "confirmation de commande", "réponse support") : elle est
 * portée par le discriminant `type` (chaîne libre déclarée par l'app). Le canal
 * (`channel`), le sens (`direction`) et l'état de remise (`deliveryStatus`) sont
 * des états métier À PART, distincts du discriminant (même logique que `order`
 * qui porte type + direction + status).
 *
 * Références vers d'autres familles = de simples ids (aucun import d'un autre
 * schéma) :
 *   - `fromRef` (émetteur), `toRefs` (destinataires) et `threadRef` (fil de
 *     conversation) sont volontairement polymorphes : l'émetteur ou un
 *     destinataire peut être un `contact`, un `worker` ou l'org, donc de simples
 *     ids que l'app résout selon le contexte (pas un `xxxId` typé sur une seule
 *     famille) ;
 *   - les liens de CONTEXTE (contactId, bookingId, invoiceId, ticketId,
 *     campaignId, reviewId) disent "de quoi parle ce message" et restent
 *     optionnels.
 *
 * Acces : la famille est HERITIERE d'un acces par fiche (`acces.ts`,
 * `docs/socle/acces-par-fiche.md`). Un message qui porte `conversationId`
 * herite des droits de sa conversation (ses participants, ou la fiche ancree)
 * et porte en REFLET `acces: { visibilite }`, recopie de la conversation par
 * le SDK et verifie par les rules. Sans `conversationId`, un message est
 * `equipe` : la porte decide, comme avant.
 *
 * Convention de patron (cf. asset) :
 *   - `xCoreFields`  : les champs MÉTIER seuls (sans les champs standard).
 *   - `XSchema`      : famille complète = champs standard + champs métier.
 *   - `XCreateInputSchema` : ce que l'appelant fournit à la création, VALIDÉ
 *     AVANT toute écriture Firestore.
 */

/** Le `type` d'un message de conversation (canal, direct, chatter) : contrat conversations du 05/10/2026. */
export const MESSAGE_TYPE_CONVERSATION = "conversation";
export const MESSAGE_TEXTE_MAX = 20_000;
/** Les auteurs RESERVES aux Functions : le client ecrit son propre personId. */
export const AUTEUR_LIA = "lia";
export const AUTEUR_SYSTEME = "systeme";

/**
 * LES REACTIONS d'un message de conversation : une liste FERMEE de quinze
 * emojis, chacun range sous une CLE courte (`pouce`, `coeur`, `mainsJointes`...), et pour
 * chaque cle la liste des personIds qui ont reagi. La cle, pas l'emoji, est le
 * nom du champ : les rules la nomment une par une (elles ne savent pas boucler
 * sur des cles inconnues), et un emoji n'est pas un nom de champ sur.
 * L'ordre compte : les quatre premiers sont ceux de la pilule au-dessus de la bulle,
 * les onze suivants sont derriere son « + » (`REACTIONS_DE_LA_PILULE` de `@taki/ui`).
 * Recopie dans `@taki/ui` (`conversations/reactions.ts`), dans les Functions
 * (`set_message_reaction`) et dans les rules ; un test compare
 * (`packages/sdk/tests/conversations-contrat.test.mjs`).
 */
export const REACTIONS_DE_MESSAGE = [
  { cle: "pouce", emoji: "\u{1F44D}" },
  { cle: "pouceBas", emoji: "\u{1F44E}" },
  { cle: "coeur", emoji: "\u2764\uFE0F" },
  { cle: "rire", emoji: "\u{1F602}" },
  { cle: "surprise", emoji: "\u{1F62E}" },
  { cle: "larmes", emoji: "\u{1F622}" },
  { cle: "mainsJointes", emoji: "\u{1F64F}" },
  { cle: "applaudissements", emoji: "\u{1F44F}" },
  { cle: "fete", emoji: "\u{1F389}" },
  { cle: "feu", emoji: "\u{1F525}" },
  { cle: "cent", emoji: "\u{1F4AF}" },
  { cle: "yeux", emoji: "\u{1F440}" },
  { cle: "reflexion", emoji: "\u{1F914}" },
  { cle: "muscle", emoji: "\u{1F4AA}" },
  { cle: "fait", emoji: "\u2705" },
] as const;
export type CleDeReaction = (typeof REACTIONS_DE_MESSAGE)[number]["cle"];
export const CLES_DE_REACTION: readonly CleDeReaction[] = REACTIONS_DE_MESSAGE.map((r) => r.cle);
/** Personnes par emoji, au plus (rules : meme plafond). */
export const REACTIONS_PAR_EMOJI_MAX = 500;
const personnesQuiReagissent = z.array(z.string().min(1)).max(REACTIONS_PAR_EMOJI_MAX);
export const reactionsSchema = z
  .object(Object.fromEntries(CLES_DE_REACTION.map((cle) => [cle, personnesQuiReagissent])) as Record<CleDeReaction, typeof personnesQuiReagissent>)
  .partial()
  .strict();
export type ReactionsDeMessage = z.output<typeof reactionsSchema>;
/** La cle d'un emoji de la liste fermee, `null` hors liste. */
export function cleDeReaction(emoji: string): CleDeReaction | null {
  return REACTIONS_DE_MESSAGE.find((r) => r.emoji === emoji || r.cle === emoji)?.cle ?? null;
}

/** La signature d'une reponse de Lia a une mention (`repondreMention`). */
export const signeLiaSchema = z
  .object({ mentionnePar: z.string().min(1), journalId: z.string().min(1).optional() })
  .strict();
export type SigneLia = z.infer<typeof signeLiaSchema>;

/**
 * LES EVENEMENTS DU SUIVI D'UNE FICHE (`docs/socle/chatter.md`), ecrits par la
 * Function `surEcritureDuChatter`, jamais par un client. La famille `fiche.*`
 * existait deja (`fiche.modifiee`, `fiche.archivee`, `fiche.restauree`,
 * `fiche.supprimee`, `fiche.liee.creee`) ; le chantier `fiche-suivi` y ajoute
 * la creation et la piece jointe. Recopies dans `functions/src/chatter/evenements.ts`
 * (`functions/` ne depend d'aucun `@taki/*`), un test compare.
 */
export const EVENEMENT_FICHE_CREEE = "fiche.creee";
export const EVENEMENT_FICHE_MODIFIEE = "fiche.modifiee";
export const EVENEMENT_FICHE_FICHIER = "fiche.fichier";
export const EVENEMENT_FICHE_LIEE_CREEE = "fiche.liee.creee";

/**
 * Combien de fiches liees un LOT garde en liste (les ids, pour ne pas compter
 * deux fois une fiche si le declencheur rejoue) ; le compte, lui, n'est pas borne.
 */
export const SUIVI_LOT_FICHES_MAX = 100;

/** Combien de jeux liés différents un lot compte (le suivant ouvre une entrée neuve). */
export const SUIVI_LOT_JEUX_MAX = 20;

/** Plafond d'une valeur mise en mots dans un changement (avant, apres, libelle). */
export const SUIVI_VALEUR_MAX = 200;
/** Plafond de champs decrits dans UN evenement de changement (le reste est compte dans le texte). */
export const SUIVI_CHANGEMENTS_MAX = 20;

/**
 * UN CHANGEMENT DE CHAMP, deja en mots par le serveur : `champ` est le chemin
 * (`status`, `customFields.statut`), `libelle`, `avant` et `apres` sont ceux
 * de la declaration effective de l'org (`suivi-mots.ts`, la seule regle des
 * mots), la clef rendue lisible en repli. Un ANCIEN message porte des clefs
 * (« Status », « aFaire ») : le volet et le centre le remettent en mots par la
 * meme regle. `null` : vide.
 */
export const changementDuSuiviSchema = z
  .object({
    champ: z.string().min(1).max(SUIVI_VALEUR_MAX),
    libelle: z.string().min(1).max(SUIVI_VALEUR_MAX),
    avant: z.string().max(SUIVI_VALEUR_MAX).nullable(),
    apres: z.string().max(SUIVI_VALEUR_MAX).nullable(),
  })
  .strict();
export type ChangementDuSuiviEcrit = z.infer<typeof changementDuSuiviSchema>;

/**
 * LE COMPTE D'UN JEU DANS UN LOT : combien de fiches de ce `famille/type`, et
 * la porte qui le montre (« Soins »), si le catalogue la donne.
 */
export const jeuDeLotSchema = z
  .object({
    jeu: z.string().min(1).max(SUIVI_VALEUR_MAX),
    libelle: z.string().min(1).max(SUIVI_VALEUR_MAX).optional(),
    nombre: z.number().int().min(1),
  })
  .strict();
export type JeuDeLot = z.infer<typeof jeuDeLotSchema>;

/**
 * UN LOT DE FICHES LIEES CREEES D'AFFILEE (`fiche.liee.creee`, chantier
 * `suivi-regroupe`) : la meme personne, a moins de dix minutes d'ecart d'une
 * fiche a la suivante, TOUS JEUX LIES CONFONDUS, et rien d'autre dans le suivi
 * entre elles, tiennent en UNE entree du fil qui compte par jeu. Ecrit et mis
 * a jour par la Function `surEcritureDuChatter` seule.
 *  - `nombre` : combien de fiches en tout (une seule : l'entree dit la fiche,
 *    comme avant) ;
 *  - `jeu` et `libelle` : le PREMIER jeu du lot (`famille/type`, sa porte) ; a
 *    eux seuls, ils decrivent un lot d'un seul jeu (la forme d'avant, qui se
 *    lit encore) ;
 *  - `jeux` : le compte par jeu, dans l'ordre ou chaque jeu est entre dans le
 *    lot (au plus `SUIVI_LOT_JEUX_MAX`). Absent : un seul jeu, `jeu` + `nombre` ;
 *  - `fiches` : `famille/id` des premieres fiches du lot (au plus
 *    `SUIVI_LOT_FICHES_MAX`) ;
 *  - `dernierAt` : l'instant de la derniere fiche du lot, d'ou part la fenetre.
 * Un evenement SANS `lot` (ecrit avant ce chantier) se lit par son texte.
 */
export const lotDeFichesLieesSchema = z
  .object({
    nombre: z.number().int().min(1),
    jeu: z.string().min(1).max(SUIVI_VALEUR_MAX),
    libelle: z.string().min(1).max(SUIVI_VALEUR_MAX).optional(),
    jeux: z.array(jeuDeLotSchema).min(1).max(SUIVI_LOT_JEUX_MAX).optional(),
    fiches: z.array(z.string().min(1).max(SUIVI_VALEUR_MAX)).max(SUIVI_LOT_FICHES_MAX),
    dernierAt: z.date().optional(),
  })
  .strict();
export type LotDeFichesLiees = z.infer<typeof lotDeFichesLieesSchema>;

/** La piece jointe d'un evenement `fiche.fichier` : son id (`files/{id}`), son nom, sa taille en octets. */
export const fichierDuSuiviSchema = z
  .object({
    id: z.string().min(1).optional(),
    nom: z.string().min(1).max(255),
    taille: z.number().int().nonnegative().optional(),
  })
  .strict();

/**
 * Un evenement systeme dans une conversation (« champ modifie », « Lia
 * indisponible »). `journalId` : la ligne du journal d'audit quand l'evenement
 * en a une (un echec de Lia APRES l'appel au modele est journalise).
 *
 * LE JOURNAL STRUCTURE du suivi (chantier `fiche-suivi`) : `par` est le
 * personId de qui a fait le geste (`lia` pour Lia, `systeme` pour une autre
 * Function ; absent si inconnu), `changements` la liste des champs changes,
 * `fichier` la piece jointe, `lot` le compte d'un lot de fiches liees creees
 * d'affilee. Tous OPTIONNELS : un ancien evenement n'a que
 * `evenement` et `details`, il reste valide et s'affiche par son texte.
 */
export const systemeSchema = z
  .object({
    evenement: z.string().min(1).max(80),
    details: z.string().max(2000).optional(),
    journalId: z.string().min(1).optional(),
    par: z.string().min(1).max(128).optional(),
    changements: z.array(changementDuSuiviSchema).max(SUIVI_CHANGEMENTS_MAX).optional(),
    fichier: fichierDuSuiviSchema.optional(),
    lot: lotDeFichesLieesSchema.optional(),
  })
  .strict();
export type EvenementSysteme = z.infer<typeof systemeSchema>;

/** Plafond du libelle d'une activite prevue (rules : meme plafond). */
export const ACTIVITE_LIBELLE_MAX = 200;

/**
 * CE QU'UNE ACTIVITE PREVUE PORTE A SA NAISSANCE (chantier `fiche-suivi`) :
 * un libelle, une echeance (date PURE `YYYY-MM-DD`, jamais un instant), une
 * personne assignee (son personId). Elle se pose par Lia ou le MCP, jamais
 * par la saisie du volet. L'appelant ne donne ni `faiteAt` ni `faitePar` :
 * celui qui ECRIT (le SDK, le serveur) pose `faiteAt: null`, et
 * `sdk.messages.marquerFaite` les change ensuite.
 */
export const activiteACreerSchema = z
  .object({
    libelle: z.string().trim().min(1).max(ACTIVITE_LIBELLE_MAX),
    echeance: z.string().refine(estDatePure, "Une échéance est une date YYYY-MM-DD.").optional(),
    assigne: z.string().min(1).max(128).optional(),
  })
  .strict();

/**
 * L'ACTIVITE telle qu'elle est stockee : la naissance, plus `faiteAt` (instant
 * serveur) et `faitePar` (l'uid de qui l'a faite). Les rules n'autorisent a
 * changer que ces deux champs, et `faitePar` est toujours l'appelant.
 *
 * `faiteAt: null` dit OUVERTE, explicitement : c'est l'egalite que lit
 * `sdk.messages.observerActivitesOuvertes` (une requete Firestore ne trouve
 * pas un champ ABSENT). Une activite nait donc avec `faiteAt: null`, et la
 * rouvrir remet `null`. Une activite ecrite avant cette regle (sans la clef)
 * reste valide et se lit ouverte, mais seule la fenetre du fil la voit.
 */
export const activiteSchema = activiteACreerSchema.extend({
  faiteAt: z.date().nullable().optional(),
  faitePar: z.string().min(1).optional(),
});
export type ActiviteDeMessage = z.infer<typeof activiteSchema>;

/** La sorte d'une entree du suivi, telle que le fil la dessine (`fiche-contrat.ts` de `@taki/ui`). */
export type SorteDuSuivi = "message" | "changement" | "evenement" | "activite";

/**
 * LA SORTE D'UN MESSAGE DU SUIVI, en pur : une activite prevue avant tout ;
 * puis un evenement systeme, qui est un CHANGEMENT quand il dit des champs
 * modifies (`fiche.modifiee`, ancien ou structure) ; sinon un message ecrit
 * par une personne ou par Lia. Les comptes de `sdk.conversations.compterLeSuivi`
 * suivent exactement ce partage (des egalites sur `auteur` et
 * `systeme.evenement`), une activite comptant parmi les messages.
 */
export function sorteDuMessageDeSuivi(message: {
  auteur?: string | undefined;
  systeme?: { evenement: string } | undefined;
  activite?: unknown;
}): SorteDuSuivi {
  if (message.activite != null) return "activite";
  if (message.auteur === AUTEUR_SYSTEME || message.systeme !== undefined) {
    return message.systeme?.evenement === EVENEMENT_FICHE_MODIFIEE ? "changement" : "evenement";
  }
  return "message";
}

/**
 * LES CHAMPS D'UN MESSAGE DE CONVERSATION (`type: 'conversation'`), de premier
 * niveau. `conversationId` (plus haut) y est obligatoire ; `parentId` dit le
 * fil ; `auteur` est un personId, ou `lia` / `systeme` (Functions seules) ;
 * `mentions` est DERIVE du texte par le SDK (`mentions.ts`) ; `activite` fait
 * du message une activite prevue du suivi.
 */
export const messageDeConversationFields = {
  /** Le message racine du fil. Absent : message racine. Immuable. */
  parentId: z.string().min(1).optional(),
  /** Le personId de l'auteur, ou `lia`, ou `systeme`. Immuable. */
  auteur: z.string().min(1).optional(),
  /** Le texte stocke, mentions en convention `@[personId]`, `@tous`, `@Lia`. */
  texte: z.string().min(1).max(MESSAGE_TEXTE_MAX).optional(),
  mentions: mentionsSchema.optional(),
  signeLia: signeLiaSchema.optional(),
  systeme: systemeSchema.optional(),
  /** Qui a reagi, par emoji. Ne s'ecrit QUE par `sdk.messages.reagir` / `retirerReaction`. */
  reactions: reactionsSchema.optional(),
  /** Une activite prevue (le suivi d'une fiche). Se marque faite par `sdk.messages.marquerFaite`, rien d'autre ne bouge. */
  activite: activiteSchema.optional(),
} as const;

/** Champs métier de la famille message (hors champs standard du socle). */
export const messageCoreFields = {
  orgId: z.string(),
  /** Discriminant de sous-type déclaré par l'app (ex. "reminder", "confirmation", "reply"). */
  type: z.string().min(1),
  /** Canal de la communication, chaîne libre pilotée par l'app (ex. "email", "sms", "notification", "portal", "call"). Optionnel. */
  channel: z.string().min(1).optional(),
  /** Sens de la communication, état métier piloté par l'app (ex. "inbound", "outbound"). Optionnel. */
  direction: z.string().min(1).optional(),
  /** Émetteur : id polymorphe (contact, worker, org...) résolu par l'app selon le contexte. Optionnel. */
  fromRef: z.string().min(1).optional(),
  /** Destinataires : liste d'ids (contacts, workers...). Optionnel. */
  toRefs: z.array(z.string()).optional(),
  /** Objet / sujet du message. Optionnel (un sms ou une notification peut ne pas en avoir). */
  subject: z.string().min(1).optional(),
  /** Corps / contenu du message. Optionnel. */
  body: z.string().min(1).optional(),
  /** Date d'envoi (ou de réception). Optionnel. */
  sentAt: z.date().optional(),
  /** État de remise, chaîne libre pilotée par l'app (ex. "queued", "sent", "delivered", "read", "failed"). Optionnel. */
  deliveryStatus: z.string().min(1).optional(),
  /** Fil de conversation de rattachement : id polymorphe (thread). Optionnel. */
  threadRef: z.string().min(1).optional(),
  /**
   * La conversation (famille `conversations`) dont ce message herite l'acces.
   * Optionnel : un message sans conversation est equipe. Immuable.
   */
  conversationId: z.string().min(1).optional(),
  /** Contact concerné par le message (réf. famille contact). Optionnel. */
  contactId: z.string().min(1).optional(),
  /** Réservation / rendez-vous lié (réf. famille booking). Optionnel. */
  bookingId: z.string().min(1).optional(),
  /** Facture liée (réf. famille invoice). Optionnel. */
  invoiceId: z.string().min(1).optional(),
  /** Ticket de support lié (réf. famille ticket). Optionnel. */
  ticketId: z.string().min(1).optional(),
  /** Campagne de rattachement (réf. famille campaign). Optionnel. */
  campaignId: z.string().min(1).optional(),
  /** Avis lié (réf. famille review). Optionnel. */
  reviewId: z.string().min(1).optional(),
  ...messageDeConversationFields,
} as const;

/**
 * Ce que `sdk.messages.envoyer` valide AVANT d'ecrire un message de
 * conversation : la conversation, le texte, le fil. L'auteur et les mentions
 * sont poses par le SDK, pas par l'appelant.
 */
export const MessageDeConversationInputSchema = z
  .object({
    conversationId: z.string().min(1),
    texte: z.string().trim().min(1).max(MESSAGE_TEXTE_MAX),
    parentId: z.string().min(1).optional(),
    mentions: mentionsSchema.partial().optional(),
  })
  .strict();
export type MessageDeConversationInput = z.infer<typeof MessageDeConversationInputSchema>;

/** Schéma complet de la famille message (champs standard + champs métier). */
export const MessageSchema = z.object({
  ...baseEntityFields,
  ...messageCoreFields,
  ...accesRefletField,
});

/** Type d'un message. */
export type Message = z.infer<typeof MessageSchema>;

/**
 * Schéma des données fournies par l'appelant à la création.
 * Les champs standard (id, dates, auteurs, archivedAt, deletedAt, schemaVersion) sont
 * remplis par le SDK et ne sont donc PAS attendus ici.
 */
export const MessageCreateInputSchema = z
  .object({
    ...messageCoreFields,
    ...customFieldsField,
  })
  .omit({ reactions: true })
  // L'appelant ne donne ni `faiteAt` ni `faitePar` : l'ecrivain pose `faiteAt: null` (ouverte).
  .extend({ activite: activiteACreerSchema.optional() });

/** Type de l'entrée de création d'un message. */
export type MessageCreateInput = z.infer<typeof MessageCreateInputSchema>;

/**
 * Schéma du patch de mise à jour (update partiel).
 * `orgId`, `type` et `conversationId` sont immuables (Security Rules) ; les
 * reactions passent par leurs verbes (`reagir`, `retirerReaction`) ; les
 * champs standard sont gérés par le SDK : aucun n'est accepté ici.
 */
export const MessageUpdateInputSchema = z
  .object({ ...messageCoreFields, ...customFieldsField })
  .omit({ orgId: true, type: true, conversationId: true, parentId: true, auteur: true, signeLia: true, systeme: true, reactions: true, activite: true })
  .partial();

/** Type du patch de mise à jour d'un message. */
export type MessageUpdateInput = z.infer<typeof MessageUpdateInputSchema>;
